import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Points / loyalty programme — mirrors stacks-agents `src/app/api/points/`. */
@Injectable({ providedIn: 'root' })
export class PointsApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  // ── read ──

  getPointsSetups(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/points`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getPointsSystem(projectId: string, systemId: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/points/system`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, systemId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  getPointsSystemTargets(projectId: string, systemId: string, targetType: 'product' | 'category'): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/points/targets`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, systemId, targetType },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getPointsCustomization(projectId: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/points/customization`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  getAllUserPointsBalances(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/points/balances`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getUserPointsBalance(projectId: string, userId: string): Promise<number> {
    return this.http
      .get<{ data?: number }>(`${environment.agentsUrl}v4/retrieval/points/balance`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, userId },
      })
      .pipe(
        map(body => body?.data ?? 0),
        catchError(() => of(0)),
      )
      .toPromise() as Promise<number>
  }

  getUserPointsLedger(projectId: string, userId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/points/ledger`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, userId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getOrderPointsLedger(projectId: string, orderId: string): Promise<{ source_type: string; points: number }[]> {
    return this.http
      .get<{ data?: { source_type: string; points: number }[] }>(`${environment.agentsUrl}v4/retrieval/points/ledger/by-order`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, orderId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<{ source_type: string; points: number }[]>
  }

  // ── write ──

  /** Inserts one or more points-ledger entries. */
  async insertPointsLedgerEntries(projectId: string, entries: Array<Record<string, any>>): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/points/ledger`,
        { projectId, entries },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Upserts the points system row. @returns The system UUID, or null on failure. */
  async upsertPointsSystem(projectId: string, systemId: string | null, mainRow: Record<string, any>): Promise<string | null> {
    const res = await this.http
      .post<{ data: { id: string } }>(`${environment.agentsUrl}v4/creation/points/system`,
        { projectId, systemId, mainRow },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return res?.data?.id ?? null
  }

  /** Bulk-upserts category/product point targets for a system. */
  async upsertPointsTargets(projectId: string, systemId: string, targets: Array<{ target_type: 'category' | 'product'; target_id: string; checked: boolean; points: number }>): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/points/targets`,
        { projectId, systemId, targets },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Upserts the points customization row for the project. */
  async upsertPointsCustomization(projectId: string, fields: Record<string, any>): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/points/customization`,
        { projectId, fields },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Enables or disables a points/loyalty setup. */
  async setPointsEnabled(projectId: string, systemId: string, enabled: boolean): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/points/enabled`,
        { projectId, systemId, enabled },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes a points/loyalty setup. */
  async deletePointsSystem(projectId: string, systemId: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/points/system`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, systemId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Removes the ledger rows tied to one order for a given source_type. */
  async deleteOrderLedgerEntries(projectId: string, orderId: string, sourceType: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/points/ledger/by-order`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, orderId, sourceType },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
